import type { ReactNode, SVGProps } from "react";

/**
 * Single consistent inline-SVG icon set for the console.
 * 16x16 grid, 1.5px stroke, round caps -- no external icon dependency.
 * Decorative by default (aria-hidden); pass a `label` for standalone icons.
 */

export type IconName =
  | "live"
  | "detail"
  | "cases"
  | "queue"
  | "gaps"
  | "topics"
  | "pim"
  | "workflows"
  | "connections"
  | "policy"
  | "analytics"
  | "compliance"
  | "settings"
  | "wizard"
  | "search"
  | "chevron-down"
  | "chevron-right"
  | "chevron-left"
  | "sort"
  | "sort-asc"
  | "sort-desc"
  | "close"
  | "plus"
  | "trash"
  | "edit"
  | "check"
  | "alert"
  | "lock"
  | "info"
  | "refresh"
  | "send"
  | "user"
  | "bot"
  | "menu"
  | "panel"
  | "arrow-right"
  | "external"
  | "empty"
  | "home"
  | "play"
  | "book"
  | "logout"
  | "sliders";

const PATHS: Record<IconName, ReactNode> = {
  live: (
    <path d="M3.2 2.5h2.1l1.1 2.8-1.5 1.2a9.5 9.5 0 0 0 4.6 4.6l1.2-1.5 2.8 1.1v2.1c0 .6-.5 1.1-1.1 1.1A11.3 11.3 0 0 1 2.1 3.6c0-.6.5-1.1 1.1-1.1Z" />
  ),
  detail: (
    <>
      <path d="M9.5 1.8H4.2c-.6 0-1 .4-1 1v10.4c0 .6.4 1 1 1h7.6c.6 0 1-.4 1-1V5.1l-3.3-3.3Z" />
      <path d="M9.4 1.9v3.4h3.3M5.5 8h5M5.5 10.5h5" />
    </>
  ),
  cases: (
    <>
      <rect x="2.8" y="3.4" width="10.4" height="10" rx="1" />
      <path d="M5.6 3.3V2.5c0-.4.3-.7.7-.7h3.4c.4 0 .7.3.7.7v.8M5.4 7.4l1.7 1.7 3.4-3.4" />
    </>
  ),
  queue: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.6V8l2.3 1.6" />
    </>
  ),
  gaps: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M6.2 6.2a1.9 1.9 0 1 1 2.6 1.8c-.5.2-.8.6-.8 1.1v.3" />
      <circle cx="8" cy="11.3" r="0.4" fill="currentColor" stroke="none" />
    </>
  ),
  topics: (
    <>
      <path d="M2.3 7.6V3.3c0-.6.4-1 1-1h4.3c.3 0 .5.1.7.3l5.1 5.1c.4.4.4 1 0 1.4l-4.3 4.3c-.4.4-1 .4-1.4 0L2.6 8.3a1 1 0 0 1-.3-.7Z" />
      <circle cx="5.7" cy="5.7" r="0.9" />
    </>
  ),
  pim: (
    <>
      <path d="M8 1.8 13.7 5v6L8 14.2 2.3 11V5L8 1.8Z" />
      <path d="M2.5 5.1 8 8.2l5.5-3.1M8 8.2v5.9" />
    </>
  ),
  workflows: (
    <>
      <circle cx="4" cy="3.6" r="1.8" />
      <circle cx="4" cy="12.4" r="1.8" />
      <circle cx="12" cy="8" r="1.8" />
      <path d="M4 5.4v5.2M5.6 4.3l4.8 2.8M5.6 11.7l4.8-2.8" />
    </>
  ),
  connections: (
    <>
      <path d="M6 1.8v3.4M10 1.8v3.4M4.4 5.2h7.2v2.2A3.6 3.6 0 0 1 8 11a3.6 3.6 0 0 1-3.6-3.6V5.2Z" />
      <path d="M8 11v3.2" />
    </>
  ),
  policy: (
    <path d="M8 1.8 13 3.6v4.1c0 3.1-2.1 5.4-5 6.5-2.9-1.1-5-3.4-5-6.5V3.6L8 1.8Z" />
  ),
  analytics: (
    <path d="M2.5 13.5h11M4.5 13.2V9.3M8 13.2V5.6M11.5 13.2V2.8" />
  ),
  compliance: (
    <>
      <path d="M8 1.8 13 3.6v4.1c0 3.1-2.1 5.4-5 6.5-2.9-1.1-5-3.4-5-6.5V3.6L8 1.8Z" />
      <path d="M5.7 7.8l1.6 1.6 3-3" />
    </>
  ),
  settings: (
    <path d="M2.5 4.7h7M11.8 4.7h1.7M9.5 2.9v3.6M2.5 11.3h1.7M6.5 11.3h7M4.2 9.5v3.6" />
  ),
  wizard: (
    <path d="M8 1.8l1.4 3.6 3.6 1.4-3.6 1.4L8 11.8 6.6 8.2 3 6.8l3.6-1.4L8 1.8ZM12.6 10.8l.6 1.5 1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6.6-1.5Z" />
  ),
  search: (
    <>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.4 10.4 14 14" />
    </>
  ),
  "chevron-down": <path d="m4 6 4 4 4-4" />,
  "chevron-right": <path d="m6 4 4 4-4 4" />,
  "chevron-left": <path d="m10 4-4 4 4 4" />,
  sort: <path d="M5.2 6.2 8 3.4l2.8 2.8M5.2 9.8 8 12.6l2.8-2.8" />,
  "sort-asc": <path d="M4.6 9.4 8 6l3.4 3.4" />,
  "sort-desc": <path d="M4.6 6.6 8 10l3.4-3.4" />,
  close: <path d="m4 4 8 8M12 4l-8 8" />,
  plus: <path d="M8 3.2v9.6M3.2 8h9.6" />,
  trash: (
    <>
      <path d="M3 4.2h10M6.4 4V2.8c0-.4.3-.8.8-.8h1.6c.5 0 .8.4.8.8V4" />
      <path d="M4.4 4.4l.5 8.3c0 .5.5 1 1 1h4.2c.5 0 1-.5 1-1l.5-8.3M6.7 7v3.8M9.3 7v3.8" />
    </>
  ),
  edit: (
    <path d="M9.8 2.8 13.2 6.2 6 13.4l-3.8.4.4-3.8 7.2-7.2ZM8.6 4l3.4 3.4" />
  ),
  check: <path d="m3.4 8.4 3 3 6.2-6.8" />,
  alert: (
    <>
      <path d="M8 2.2 14.5 13.4H1.5L8 2.2Z" />
      <path d="M8 6.5v3.1" />
      <circle cx="8" cy="11.6" r="0.4" fill="currentColor" stroke="none" />
    </>
  ),
  lock: (
    <>
      <rect x="3.4" y="7" width="9.2" height="6.6" rx="1" />
      <path d="M5.4 7V5a2.6 2.6 0 0 1 5.2 0v2M8 9.6v1.6" />
    </>
  ),
  info: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 7.4v3.4" />
      <circle cx="8" cy="5.2" r="0.4" fill="currentColor" stroke="none" />
    </>
  ),
  refresh: (
    <path d="M13.4 8a5.4 5.4 0 1 1-1.6-3.8M13.4 2.6v3.2h-3.2" />
  ),
  send: <path d="M14 2 7.3 8.7M14 2 9.7 14l-2.4-5.3L2 6.3 14 2Z" />,
  home: (
    <>
      <path d="M2.5 7.5 8 3l5.5 4.5" />
      <path d="M4 7v6.5h8V7" />
      <path d="M6.8 13.5v-3.6h2.4v3.6" />
    </>
  ),
  play: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M6.6 5.6v4.8L10.4 8z" fill="currentColor" stroke="none" />
    </>
  ),
  book: (
    <>
      <path d="M3 3.2h4.2A1.8 1.8 0 0 1 9 5v8.4a1.4 1.4 0 0 0-1.4-1.2H3z" />
      <path d="M13 3.2H8.8A1.8 1.8 0 0 0 7 5v8.4a1.4 1.4 0 0 1 1.4-1.2H13z" />
    </>
  ),
  logout: (
    <>
      <path d="M6.5 2.8H3.5v10.4h3" />
      <path d="M9.4 5.2 12.2 8l-2.8 2.8M12.2 8H6.2" />
    </>
  ),
  sliders: (
    <>
      <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
      <circle cx="6" cy="4.5" r="1.4" fill="var(--c-surface)" />
      <circle cx="10.5" cy="8" r="1.4" fill="var(--c-surface)" />
      <circle cx="5" cy="11.5" r="1.4" fill="var(--c-surface)" />
    </>
  ),
  user: (
    <>
      <circle cx="8" cy="5.2" r="2.8" />
      <path d="M2.8 13.8a5.2 5.2 0 0 1 10.4 0" />
    </>
  ),
  bot: (
    <>
      <rect x="3" y="5" width="10" height="7.6" rx="1.6" />
      <path d="M8 2.4V5M6 12.6v1M10 12.6v1" />
      <circle cx="6.2" cy="8.4" r="0.5" fill="currentColor" stroke="none" />
      <circle cx="9.8" cy="8.4" r="0.5" fill="currentColor" stroke="none" />
    </>
  ),
  menu: <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />,
  panel: (
    <>
      <rect x="2" y="2.8" width="12" height="10.4" rx="1.2" />
      <path d="M6 3v10" />
    </>
  ),
  "arrow-right": <path d="M2.5 8h11M9.5 4l4 4-4 4" />,
  external: (
    <path d="M6.5 3.5H3.4c-.5 0-.9.4-.9.9v8.2c0 .5.4.9.9.9h8.2c.5 0 .9-.4.9-.9V9.5M9.5 2.5h4v4M13.2 2.8 7.6 8.4" />
  ),
  empty: (
    <>
      <path d="M2.4 8.6 4 4a1 1 0 0 1 .9-.7h6.2a1 1 0 0 1 .9.7l1.6 4.6" />
      <path d="M2.4 8.6h3.4c.2 1 1 1.8 2.2 1.8s2-.8 2.2-1.8h3.4v3.2c0 .6-.5 1.1-1.1 1.1H3.5c-.6 0-1.1-.5-1.1-1.1V8.6Z" />
    </>
  ),
};

export function Icon({
  name,
  size = 16,
  label,
  ...rest
}: {
  name: IconName;
  size?: number;
  /** Accessible label; omit for purely decorative icons. */
  label?: string;
} & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      aria-label={label}
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
